import { supabase } from '../integrations/supabase/client';

// Public provider data - used for listings and public profile views
export interface ServiceProvider {
  id: string;
  user_id: string;
  provider_type: 'veterinarian_individual' | 'veterinary_clinic' | 'pet_store' | 'dog_walker' | 'grooming_spa';
  business_name: string;
  description?: string;
  photos: string[];
  services: string[];
  languages: string[];
  latitude: number;
  longitude: number;
  address?: string; // Public address (city level)
  city?: string;
  state?: string;
  country?: string;
  schedule: {
    monday: { open: string | null; close: string | null; closed: boolean };
    tuesday: { open: string | null; close: string | null; closed: boolean };
    wednesday: { open: string | null; close: string | null; closed: boolean };
    thursday: { open: string | null; close: string | null; closed: boolean };
    friday: { open: string | null; close: string | null; closed: boolean };
    saturday: { open: string | null; close: string | null; closed: boolean };
    sunday: { open: string | null; close: string | null; closed: boolean };
    emergency_available?: boolean;
  };
  rating: number;
  total_reviews: number;
  total_views: number;
  total_contacts: number;
  verified: boolean;
  distance_km?: number;
}

// Full provider data (with PII) - only for provider's own dashboard
export interface ServiceProviderFull extends ServiceProvider {
  phone: string;
  email: string;
  whatsapp?: string;
  credential_document_url?: string;
  registration_number?: string;
}

export interface ProviderSearchParams {
  user_lat: number;
  user_lon: number;
  provider_type?: string;
  emergency_only?: boolean;
  max_distance_km?: number;
  open_now?: boolean;
}

export const searchProviders = async (params: ProviderSearchParams): Promise<ServiceProvider[]> => {
  const { data, error } = await supabase.functions.invoke('find-service-providers', {
    body: params
  });

  if (error) throw error;
  return data.providers || [];
};

export const getProviderById = async (providerId: string): Promise<ServiceProvider | null> => {
  // Only fetch public-safe fields - PII is fetched on-demand via get-provider-contact edge function
  // Use the safe public view (excludes phone, email, ID documents, payment IDs)
  const { data, error } = await supabase
    .from('service_providers_public' as never)
    .select(`
      id, user_id, provider_type, business_name, description,
      photos, services, languages, latitude, longitude,
      city, state, country, schedule, rating, total_reviews,
      total_views, total_contacts, verified
    `)
    .eq('id', providerId)
    .single();

  if (error) throw error;
  return data as unknown as ServiceProvider;
};

export const trackProviderContact = async (providerId: string, contactType: 'phone_view' | 'whatsapp_click' | 'email_click' | 'map_view') => {
  const { error } = await supabase.functions.invoke('track-provider-contact', {
    body: { provider_id: providerId, contact_type: contactType }
  });

  if (error) throw error;
};

export const submitProviderReview = async (providerId: string, rating: number, comment?: string) => {
  const { error } = await supabase.functions.invoke('submit-provider-review', {
    body: { provider_id: providerId, rating, comment }
  });

  if (error) throw error;
};

export const getProviderReviews = async (providerId: string) => {
  const { data, error } = await supabase
    .from('provider_reviews')
    .select(`
      *,
      users (
        id,
        display_name,
        avatar_url
      )
    `)
    .eq('provider_id', providerId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data;
};

export const registerProvider = async (providerData: Partial<ServiceProvider>) => {
  const { data, error } = await supabase.functions.invoke('register-provider', {
    body: providerData
  });

  if (error) throw error;
  return data;
};

export const uploadProviderPhoto = async (file: File, userId: string): Promise<string> => {
  console.log('🔵 [UPLOAD PHOTO] Starting photo upload:', {
    fileName: file.name,
    fileSize: file.size,
    fileType: file.type,
    userId
  });

  // Get current session to ensure we have fresh token
  const { data: { session } } = await supabase.auth.getSession();
  
  if (!session) {
    console.error('❌ [UPLOAD PHOTO] No active session');
    throw new Error('No active session. Please log in.');
  }

  console.log('✅ [UPLOAD PHOTO] Session token exists');

  // Normalize MIME type
  let mimeType = file.type || '';
  if (mimeType === 'image/jpg') {
    mimeType = 'image/jpeg';
  }
  
  // If no MIME type, infer from extension
  const fileExt = file.name.split('.').pop()?.toLowerCase() || 'jpg';
  if (!mimeType || mimeType === '') {
    if (fileExt === 'jpg' || fileExt === 'jpeg') mimeType = 'image/jpeg';
    else if (fileExt === 'png') mimeType = 'image/png';
    else if (fileExt === 'webp') mimeType = 'image/webp';
  }

  console.log('🔵 [UPLOAD PHOTO] Normalized MIME type:', mimeType);

  // Validate file type
  const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/jpg'];
  if (!allowedTypes.includes(mimeType)) {
    const error = `Invalid file type: ${mimeType}. Allowed: ${allowedTypes.join(', ')}`;
    console.error('❌ [UPLOAD PHOTO]', error);
    throw new Error(error);
  }

  // Validate file size (50MB = 52428800 bytes)
  if (file.size > 52428800) {
    const error = `File too large: ${(file.size / 1048576).toFixed(2)}MB. Max: 50MB`;
    console.error('❌ [UPLOAD PHOTO]', error);
    throw new Error(error);
  }

  const fileName = `${userId}/${Date.now()}.${fileExt}`;
  console.log('🔵 [UPLOAD PHOTO] Generated path:', fileName);

  const { data, error } = await supabase.storage
    .from('provider-photos')
    .upload(fileName, file, {
      contentType: mimeType,
      cacheControl: '3600',
      upsert: false
    });

  if (error) {
    console.error('❌ [UPLOAD PHOTO] Supabase Storage Error:', {
      message: error.message,
      name: error.name,
      fullError: error
    });
    throw error;
  }

  console.log('✅ [UPLOAD PHOTO] Upload successful:', data);

  const { data: { publicUrl } } = supabase.storage
    .from('provider-photos')
    .getPublicUrl(fileName);

  console.log('✅ [UPLOAD PHOTO] Public URL:', publicUrl);

  return publicUrl;
};

export const uploadProviderCredential = async (file: File, userId: string): Promise<string> => {
  console.log('🔵 [UPLOAD CREDENTIAL] Starting credential upload:', {
    fileName: file.name,
    fileSize: file.size,
    fileType: file.type,
    userId
  });

  // Get current session to ensure we have fresh token
  const { data: { session } } = await supabase.auth.getSession();
  
  if (!session) {
    console.error('❌ [UPLOAD CREDENTIAL] No active session');
    throw new Error('No active session. Please log in.');
  }

  console.log('✅ [UPLOAD CREDENTIAL] Session token exists');

  // Normalize MIME type
  let mimeType = file.type || '';
  if (mimeType === 'image/jpg') {
    mimeType = 'image/jpeg';
  }
  
  // If no MIME type, infer from extension
  const fileExt = file.name.split('.').pop()?.toLowerCase() || 'jpg';
  if (!mimeType || mimeType === '') {
    if (fileExt === 'jpg' || fileExt === 'jpeg') mimeType = 'image/jpeg';
    else if (fileExt === 'png') mimeType = 'image/png';
    else if (fileExt === 'webp') mimeType = 'image/webp';
    else if (fileExt === 'pdf') mimeType = 'application/pdf';
  }

  console.log('🔵 [UPLOAD CREDENTIAL] Normalized MIME type:', mimeType);

  // Validate file type
  const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/jpg', 'application/pdf'];
  if (!allowedTypes.includes(mimeType)) {
    const error = `Invalid file type: ${mimeType}. Allowed: ${allowedTypes.join(', ')}`;
    console.error('❌ [UPLOAD CREDENTIAL]', error);
    throw new Error(error);
  }

  // Validate file size (50MB = 52428800 bytes)
  if (file.size > 52428800) {
    const error = `File too large: ${(file.size / 1048576).toFixed(2)}MB. Max: 50MB`;
    console.error('❌ [UPLOAD CREDENTIAL]', error);
    throw new Error(error);
  }

  const fileName = `${userId}/credential_${Date.now()}.${fileExt}`;
  console.log('🔵 [UPLOAD CREDENTIAL] Generated path:', fileName);

  const { data, error } = await supabase.storage
    .from('provider-credentials')
    .upload(fileName, file, {
      contentType: mimeType,
      cacheControl: '3600',
      upsert: false
    });

  if (error) {
    console.error('❌ [UPLOAD CREDENTIAL] Supabase Storage Error:', {
      message: error.message,
      name: error.name,
      fullError: error
    });
    throw error;
  }

  console.log('✅ [UPLOAD CREDENTIAL] Upload successful:', data);

  const { data: { publicUrl } } = supabase.storage
    .from('provider-credentials')
    .getPublicUrl(fileName);

  console.log('✅ [UPLOAD CREDENTIAL] Public URL:', publicUrl);

  return publicUrl;
};

export const uploadProviderIdDocument = async (
  file: File, 
  userId: string, 
  documentType: 'front' | 'back'
): Promise<string> => {
  console.log(`🔵 [UPLOAD ID ${documentType.toUpperCase()}] Starting ID document upload:`, {
    fileName: file.name,
    fileSize: file.size,
    fileType: file.type,
    userId,
    documentType
  });

  // Get current session to ensure we have fresh token
  const { data: { session } } = await supabase.auth.getSession();
  
  if (!session) {
    console.error(`❌ [UPLOAD ID ${documentType.toUpperCase()}] No active session`);
    throw new Error('No active session. Please log in.');
  }

  console.log(`✅ [UPLOAD ID ${documentType.toUpperCase()}] Session token exists`);

  // Normalize MIME type
  let mimeType = file.type || '';
  if (mimeType === 'image/jpg') {
    mimeType = 'image/jpeg';
  }
  
  // If no MIME type, infer from extension
  const fileExt = file.name.split('.').pop()?.toLowerCase() || 'jpg';
  if (!mimeType || mimeType === '') {
    if (fileExt === 'jpg' || fileExt === 'jpeg') mimeType = 'image/jpeg';
    else if (fileExt === 'png') mimeType = 'image/png';
    else if (fileExt === 'webp') mimeType = 'image/webp';
    else if (fileExt === 'pdf') mimeType = 'application/pdf';
  }

  console.log(`🔵 [UPLOAD ID ${documentType.toUpperCase()}] Normalized MIME type:`, mimeType);

  // Validate file type
  const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/jpg', 'application/pdf'];
  if (!allowedTypes.includes(mimeType)) {
    const error = `Invalid file type: ${mimeType}. Allowed: ${allowedTypes.join(', ')}`;
    console.error(`❌ [UPLOAD ID ${documentType.toUpperCase()}]`, error);
    throw new Error(error);
  }

  // Validate file size (5MB = 5242880 bytes for secure ID documents)
  if (file.size > 5242880) {
    const error = `File too large: ${(file.size / 1048576).toFixed(2)}MB. Max: 5MB for ID documents`;
    console.error(`❌ [UPLOAD ID ${documentType.toUpperCase()}]`, error);
    throw new Error(error);
  }

  const fileName = `${userId}/id_${documentType}_${Date.now()}.${fileExt}`;
  console.log(`🔵 [UPLOAD ID ${documentType.toUpperCase()}] Generated path:`, fileName);

  const { data, error } = await supabase.storage
    .from('provider-id-documents')
    .upload(fileName, file, {
      contentType: mimeType,
      cacheControl: '3600',
      upsert: false
    });

  if (error) {
    console.error(`❌ [UPLOAD ID ${documentType.toUpperCase()}] Supabase Storage Error:`, {
      message: error.message,
      name: error.name,
      fullError: error
    });
    throw error;
  }

  console.log(`✅ [UPLOAD ID ${documentType.toUpperCase()}] Upload successful to private bucket:`, data);

  // For private buckets, store the path (signed URLs will be generated when needed for admin viewing)
  const storagePath = `provider-id-documents/${fileName}`;
  
  console.log(`✅ [UPLOAD ID ${documentType.toUpperCase()}] Storage path:`, storagePath);

  return storagePath;
};
